import { useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import { Modal } from "./ui.js";

type Mode = "quick" | "meal" | "restaurant";
interface Meal { id: string; name: string; totals: { calories: number; proteinG: number } }
interface Restaurant { id: string; name: string; orders: { id: string; name: string; calories: number | null }[] }

const SLOTS: [string, string][] = [
  ["breakfast", "Breakfast"],
  ["lunch", "Lunch"],
  ["dinner", "Dinner"],
  ["snack", "Snack"]
];

export function FoodLogModal({
  date,
  defaultSlot = "snack",
  onClose,
  onLogged
}: {
  date?: string;
  defaultSlot?: string;
  onClose: () => void;
  onLogged: () => void;
}) {
  const [mode, setMode] = useState<Mode>("quick");
  const [slot, setSlot] = useState(defaultSlot);
  const [form, setForm] = useState<Record<string, string>>({});
  const [meals, setMeals] = useState<Meal[]>([]);
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [selectedId, setSelectedId] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode === "meal" && meals.length === 0) api.get<{ meals: Meal[] }>("/api/meals").then((r) => setMeals(r.meals)).catch(() => undefined);
    if (mode === "restaurant" && restaurants.length === 0) api.get<{ restaurants: Restaurant[] }>("/api/restaurants").then((r) => setRestaurants(r.restaurants)).catch(() => undefined);
  }, [mode, meals.length, restaurants.length]);

  function num(k: string): number | undefined {
    const v = form[k];
    return v === undefined || v === "" ? undefined : Number(v);
  }

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      let payload: Record<string, unknown> = { date, slot };
      if (mode === "quick") {
        if (!form.description) throw new Error("Add a description.");
        payload = { ...payload, source: "quick", description: form.description, calories: num("calories") ?? 0, proteinG: num("proteinG") ?? 0, carbsG: num("carbsG") ?? 0, fatG: num("fatG") ?? 0, fiberG: num("fiberG") ?? 0 };
      } else if (mode === "meal") {
        if (!selectedId) throw new Error("Pick a meal.");
        payload = { ...payload, mealId: selectedId };
      } else {
        if (!selectedId) throw new Error("Pick an order.");
        payload = { ...payload, orderId: selectedId };
      }
      await api.post("/api/nutrition/log", payload);
      onLogged();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : "Could not log.");
      setSaving(false);
    }
  }

  const allOrders = restaurants.flatMap((r) => r.orders.map((o) => ({ ...o, restaurant: r.name })));

  return (
    <Modal title="Log food" onClose={onClose}>
      {error && <div className="alert alert-danger">{error}</div>}

      <div className="segmented" style={{ marginBottom: "var(--space-4)" }}>
        {(["quick", "meal", "restaurant"] as Mode[]).map((m) => (
          <button key={m} className={mode === m ? "active" : ""} onClick={() => setMode(m)} type="button">
            {m === "quick" ? "Quick" : m === "meal" ? "Saved meal" : "Restaurant"}
          </button>
        ))}
      </div>

      <div className="field">
        <label>Meal</label>
        <select className="select" value={slot} onChange={(e) => setSlot(e.target.value)}>
          {SLOTS.map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
      </div>

      {mode === "quick" && (
        <>
          <div className="field">
            <label>What did you eat?</label>
            <input className="input" value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Greek yogurt with berries" />
          </div>
          <div className="row wrap">
            {([["calories", "Calories"], ["proteinG", "Protein (g)"], ["carbsG", "Carbs (g)"], ["fatG", "Fat (g)"]] as const).map(([k, l]) => (
              <div className="field grow" key={k} style={{ marginBottom: "var(--space-3)", minWidth: 120 }}>
                <label>{l}</label>
                <input className="input" type="number" inputMode="decimal" value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
              </div>
            ))}
          </div>
          <p className="dim text-xs">No barcode scanning yet — that arrives later. For now, enter what you know.</p>
        </>
      )}

      {mode === "meal" && (
        <div className="field">
          <label>Your saved meals</label>
          {meals.length === 0 ? (
            <p className="muted text-sm">No saved meals yet — create one under Meals.</p>
          ) : (
            <select className="select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              <option value="">Choose a meal…</option>
              {meals.map((m) => (
                <option key={m.id} value={m.id}>{m.name} · {m.totals.calories} kcal</option>
              ))}
            </select>
          )}
        </div>
      )}

      {mode === "restaurant" && (
        <div className="field">
          <label>Your saved orders</label>
          {allOrders.length === 0 ? (
            <p className="muted text-sm">No saved restaurant orders yet — add them under Restaurants.</p>
          ) : (
            <select className="select" value={selectedId} onChange={(e) => setSelectedId(e.target.value)}>
              <option value="">Choose an order…</option>
              {allOrders.map((o) => (
                <option key={o.id} value={o.id}>{o.restaurant}: {o.name}{o.calories ? ` · ${o.calories} kcal` : ""}</option>
              ))}
            </select>
          )}
        </div>
      )}

      <div className="row mt-4">
        <button className="btn btn-primary grow" onClick={submit} disabled={saving}>{saving ? "Logging…" : "Log it"}</button>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
      </div>
    </Modal>
  );
}
